import type { Area } from 'react-easy-crop';

export async function createImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = src;
  });
}

export function getCroppedImage(
  imageSrc: string,
  pixelCrop: Area,
): Promise<string | null> {
  return createImage(imageSrc)
    .then((image) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        return null;
      }

      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;

      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height,
      );

      return canvas.toDataURL('image/jpeg');
    })
    .then((croppedImage) => {
      if (croppedImage) {
        return downscaleImage(croppedImage, 600, 600);
      }
      return null;
    });
}

function downscaleImage(
  src: string,
  maxWidth: number,
  maxHeight: number,
): Promise<string> {
  return createImage(src).then((image) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Clamp at 1 so small sources are preserved at their native size
    // instead of being canvas-upscaled into blurry pixels.
    const scaleFactor = Math.min(
      maxWidth / image.width,
      maxHeight / image.height,
      1,
    );

    canvas.width = image.width * scaleFactor;
    canvas.height = image.height * scaleFactor;

    ctx?.drawImage(image, 0, 0, canvas.width, canvas.height);

    return canvas.toDataURL('image/jpeg');
  });
}

export async function resizeImage(base64Image: string): Promise<string> {
  const targetSize = 600;

  const image = await createImage(base64Image);

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  // Clamp at 1 so small sources stay at native size rather than being
  // upscaled to a blurry 600px on the smaller axis.
  const rawScale =
    image.width < image.height
      ? targetSize / image.width
      : targetSize / image.height;
  const scaleFactor = Math.min(rawScale, 1);

  canvas.width = image.width * scaleFactor;
  canvas.height = image.height * scaleFactor;

  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  return canvas.toDataURL('image/jpeg');
}
